/**
 * ArchitecturePanel — slides in from the right and renders the full
 * Nabhos Platform Architecture & Design Reference document as rich,
 * structured React. All content is sourced from docs/ARCHITECTURE.md.
 *
 * Rendered as a right-side panel (using the existing SlideOutPanel shell)
 * with its own sticky left-navigation for the 14 sections.
 */

import { useState, useRef, useEffect } from 'react';
import { X, ExternalLink } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface NavItem { id: string; label: string; }

const NAV: NavItem[] = [
  { id: 's1',  label: '1 · Platform Overview' },
  { id: 's2',  label: '2 · System Boundaries' },
  { id: 's3',  label: '3 · Intelligence Loop' },
  { id: 's4',  label: '4 · High-Level Architecture' },
  { id: 's5',  label: '5 · Component Design' },
  { id: 's6',  label: '6 · Data Architecture' },
  { id: 's7',  label: '7 · Integration Architecture' },
  { id: 's8',  label: '8 · Security Architecture' },
  { id: 's9',  label: '9 · Deployment Architecture' },
  { id: 's10', label: '10 · GIS Sub-Platform' },
  { id: 's11', label: '11 · Design System' },
  { id: 's12', label: '12 · Implementation Status' },
  { id: 's13', label: '13 · Architectural Decisions' },
  { id: 's14', label: '14 · Document Registry' },
];

// ─── Shared sub-components ────────────────────────────────────────────────────

function Anchor({ id }: { id: string }) {
  return <div id={id} className="scroll-mt-4" />;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-base font-bold text-gray-900 dark:text-gray-100 mt-8 mb-2 pb-1.5 border-b border-gray-200 dark:border-gray-700 first:mt-0">
      {children}
    </h2>
  );
}

function SubTitle({ children }: { children: React.ReactNode }) {
  return <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mt-4 mb-1.5">{children}</h3>;
}

function Para({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-2">{children}</p>;
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-l-4 border-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-r px-3 py-2 my-2 text-xs text-gray-700 dark:text-gray-300 leading-relaxed italic">
      {children}
    </div>
  );
}

function Warn({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-l-4 border-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-r px-3 py-2 my-2 text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
      {children}
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: (string | React.ReactNode)[][] }) {
  return (
    <div className="overflow-x-auto my-3">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} className="bg-gray-100 dark:bg-gray-800 text-left px-2.5 py-1.5 border border-gray-200 dark:border-gray-700 font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className={ri % 2 === 1 ? 'bg-gray-50 dark:bg-gray-800/40' : ''}>
              {row.map((cell, ci) => (
                <td key={ci} className="px-2.5 py-1.5 border border-gray-200 dark:border-gray-700 align-top text-gray-700 dark:text-gray-300">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Code({ children }: { children: string }) {
  return (
    <pre className="my-3 p-3 rounded-lg bg-gray-900 dark:bg-gray-950 text-green-400 text-[10px] font-mono leading-relaxed overflow-x-auto whitespace-pre">
      {children}
    </pre>
  );
}

function Ul({ items }: { items: string[] }) {
  return (
    <ul className="my-2 space-y-1">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-1.5 text-sm text-gray-700 dark:text-gray-300">
          <span className="text-gray-400 mt-1 flex-shrink-0">·</span>
          <span className="leading-relaxed">{item}</span>
        </li>
      ))}
    </ul>
  );
}

function Badge({ color, children }: { color: 'green' | 'amber' | 'red' | 'blue' | 'gray' | 'purple'; children: React.ReactNode }) {
  const cls = {
    green:  'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
    amber:  'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
    red:    'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
    blue:   'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
    gray:   'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400',
    purple: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
  }[color];
  return <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 rounded ${cls}`}>{children}</span>;
}

// Flow diagram — renders the ASCII-art intelligence loop as a styled visual
function IntelligenceLoopDiagram() {
  const steps = [
    { n: '1', title: 'Define Area',            sub: 'Boundary + metadata per vertical', col: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' },
    { n: '2', title: 'Satellite Survey',       sub: 'Sentinel-2 · Landsat · MODIS (GEE)', col: 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800' },
    { n: '3', title: 'AI Anomaly Analysis',    sub: 'Index delta · AI narrative · confidence', col: 'bg-violet-50 dark:bg-violet-900/20 border-violet-200 dark:border-violet-800' },
    { n: '4', title: 'Human Gate 1',           sub: 'Confirm / reject / defer / escalate', col: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' },
    { n: '5', title: 'Drone Survey Planning',  sub: 'AI payload · flight path · weather', col: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800' },
    { n: '6', title: 'Fly + Upload',           sub: 'Telemetry ingestion · SHA-256 sealing', col: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' },
    { n: '7', title: 'Human Gate 2',           sub: 'Verify drone findings · sign-off', col: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' },
    { n: '8', title: 'Respond / Remediate',    sub: 'Spray · enforce · repair · dispatch', col: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' },
    { n: '9', title: 'Ask Nabhos',             sub: 'NLQ across all steps · all history', col: 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800' },
  ];
  return (
    <div className="my-4">
      <div className="grid grid-cols-3 gap-1.5">
        {steps.map((s) => (
          <div key={s.n} className={`rounded-lg border p-2.5 ${s.col}`}>
            <div className="flex items-center gap-1.5 mb-1">
              <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0" style={{ background: '#276749' }}>
                {s.n}
              </span>
              <p className="text-[11px] font-semibold text-gray-900 dark:text-gray-100 leading-tight">{s.title}</p>
            </div>
            <p className="text-[9px] text-gray-500 dark:text-gray-400 leading-tight pl-6">{s.sub}</p>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-gray-400 dark:text-gray-500 text-center mt-2">
        Steps 1 → 8 form the detection-to-remediation chain · Step 9 provides NLQ access across all history
      </p>
    </div>
  );
}

// Flow diagram — System Boundaries
function SystemBoundaryDiagram() {
  return (
    <div className="my-3 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden text-xs">
      {/* Main Platform box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-gray-200 dark:border-gray-700 px-3 py-1.5">
        <p className="font-bold text-blue-800 dark:text-blue-300 text-[10px] uppercase tracking-wide">Main Platform (src/backend + src/frontend)</p>
      </div>
      <div className="grid grid-cols-4 gap-0 border-b border-gray-200 dark:border-gray-700">
        {['React Web App', 'Express REST API', 'PostgreSQL + PostGIS + pgvector', 'Supabase / GCS Storage'].map((n) => (
          <div key={n} className="px-2.5 py-2 border-r last:border-r-0 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <p className="text-gray-700 dark:text-gray-300 leading-snug">{n}</p>
          </div>
        ))}
      </div>
      {/* Connector */}
      <div className="flex items-center justify-center py-1.5 bg-gray-50 dark:bg-gray-800 gap-2 border-b border-gray-200 dark:border-gray-700">
        <span className="text-[10px] text-gray-500 dark:text-gray-400 font-mono">Deep-link + JWT ↓ · Asset Handoff ↓</span>
      </div>
      {/* GIS Platform box */}
      <div className="bg-green-50 dark:bg-green-900/20 border-b border-gray-200 dark:border-gray-700 px-3 py-1.5">
        <p className="font-bold text-green-800 dark:text-green-300 text-[10px] uppercase tracking-wide">GIS Sub-Platform (src/gis-platform) — independently deployable</p>
      </div>
      <div className="grid grid-cols-4 gap-0">
        {['MapLibre Web Client', 'GIS Express API', 'PostGIS DB (assets · layers)', 'Ingestion Worker (tiles · COGs)'].map((n) => (
          <div key={n} className="px-2.5 py-2 border-r last:border-r-0 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <p className="text-gray-700 dark:text-gray-300 leading-snug">{n}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// Architecture overview diagram — simplified high-level boxes
function HighLevelDiagram() {
  return (
    <div className="my-3 space-y-2 text-xs">
      {/* External sources */}
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-2.5">
        <p className="text-[9px] font-bold uppercase tracking-wide text-gray-400 mb-1.5">External Data Sources</p>
        <div className="flex flex-wrap gap-1.5">
          {['Google Earth Engine (Sentinel-2 · Landsat · MODIS)', 'OpenWeather API', 'Copernicus Hub (free ESA)'].map((s) => (
            <span key={s} className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">{s}</span>
          ))}
        </div>
      </div>
      {/* Arrow */}
      <div className="text-center text-gray-400 text-sm">↓</div>
      {/* Nabhos platform */}
      <div className="rounded-lg border border-blue-200 dark:border-blue-800 overflow-hidden">
        <div className="bg-blue-50 dark:bg-blue-900/20 px-3 py-1">
          <p className="text-[9px] font-bold uppercase tracking-wide text-blue-700 dark:text-blue-300">Nabhos Main Platform</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y divide-gray-200 dark:divide-gray-700">
          {[
            { label: 'Frontend', val: 'React 18 · Vite · TailwindCSS · Leaflet' },
            { label: 'API Gateway', val: 'Express · TypeScript · Zod · JWT/MFA/RLS' },
            { label: 'Business Logic', val: '30+ services: Mission · Satellite · Anomaly · NLQ · Ingest · Reports' },
            { label: 'AI / ML', val: 'Gemini · Change Detection · Index Calculator' },
          ].map((item) => (
            <div key={item.label} className="px-2.5 py-2 bg-white dark:bg-gray-900">
              <p className="text-[10px] font-semibold text-gray-700 dark:text-gray-300 mb-0.5">{item.label}</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-snug">{item.val}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 divide-x divide-gray-200 dark:divide-gray-700 border-t border-gray-200 dark:border-gray-700">
          {[
            { label: 'Storage', val: 'PostgreSQL 15 + PostGIS + pgvector · Supabase / GCS bucket: mission-images' },
            { label: 'Providers', val: 'Gemini (default) · Cloudflare Workers AI · Groq · OpenRouter' },
          ].map((item) => (
            <div key={item.label} className="px-2.5 py-2 bg-gray-50 dark:bg-gray-800/50">
              <p className="text-[10px] font-semibold text-gray-700 dark:text-gray-300 mb-0.5">{item.label}</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-snug">{item.val}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Arrow */}
      <div className="text-center text-gray-400 text-sm">↓ Deep-link + JWT ↓ Asset Handoff</div>
      {/* GIS sub-platform */}
      <div className="rounded-lg border border-green-200 dark:border-green-800 overflow-hidden">
        <div className="bg-green-50 dark:bg-green-900/20 px-3 py-1">
          <p className="text-[9px] font-bold uppercase tracking-wide text-green-700 dark:text-green-300">GIS Sub-Platform (independent)</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-gray-200 dark:divide-gray-700">
          {['MapLibre GL Web', 'GIS Express API (30+ endpoints)', 'PostGIS DB (assets · layers · boundaries)', 'Ingestion Worker (tile gen · COG)'].map((n) => (
            <div key={n} className="px-2.5 py-2 bg-white dark:bg-gray-900">
              <p className="text-[10px] text-gray-600 dark:text-gray-400 leading-snug">{n}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ERD simplified visual
function ERDiagram() {
  const tables = [
    { name: 'organizations', color: 'border-blue-400', cols: ['id UUID PK', 'name', 'metadata JSONB'] },
    { name: 'users', color: 'border-blue-400', cols: ['id UUID PK', 'email', 'password_hash', 'role'] },
    { name: 'missions', color: 'border-green-400', cols: ['id UUID PK', 'tenant_id FK', 'name', 'status', 'metadata JSONB'] },
    { name: 'satellite_boundaries', color: 'border-indigo-400', cols: ['id UUID PK', 'tenant_id FK', 'name', 'geometry GEOMETRY(Polygon,4326)', 'metadata JSONB'] },
    { name: 'satellite_imagery', color: 'border-indigo-400', cols: ['id UUID PK', 'boundary_id FK', 'source', 'acquisition_date', 'ndvi_value', 'ndwi_value', 'lst_value'] },
    { name: 'detected_anomalies', color: 'border-red-400', cols: ['id UUID PK', 'boundary_id FK', 'severity', 'ai_narrative TEXT', 'status', 'probable_causes JSONB'] },
    { name: 'anomaly_verifications', color: 'border-amber-400', cols: ['id UUID PK', 'anomaly_id FK', 'verified_by FK', 'action', 'notes', 'created_at (immutable)'] },
    { name: 'conversation_sessions', color: 'border-teal-400', cols: ['id UUID PK', 'tenant_id FK', 'context JSONB'] },
    { name: 'digital_seals', color: 'border-gray-400', cols: ['id UUID PK', 'file_id FK', 'sha256_hash', 'algorithm', 'created_at'] },
  ];
  return (
    <div className="my-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
      {tables.map((t) => (
        <div key={t.name} className={`rounded border-l-4 ${t.color} bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700`}>
          <div className="px-2.5 py-1 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <p className="text-[10px] font-bold font-mono text-gray-800 dark:text-gray-200">{t.name}</p>
          </div>
          <div className="px-2.5 py-1.5 space-y-0.5">
            {t.cols.map((c) => (
              <p key={c} className="text-[9px] font-mono text-gray-500 dark:text-gray-400">{c}</p>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Deployment topology
function DeploymentDiagram() {
  const services = [
    { name: 'nabhos-api', tech: 'Express Node 20', scale: 'Cloud Run min 1', color: 'bg-blue-100 dark:bg-blue-900/30' },
    { name: 'nabhos-web', tech: 'nginx + Vite SPA', scale: 'Cloud Run min 1', color: 'bg-blue-100 dark:bg-blue-900/30' },
    { name: 'nabhos-ingestion', tech: 'Express Node 20', scale: 'Cloud Run min 0', color: 'bg-blue-100 dark:bg-blue-900/30' },
    { name: 'nabhos-gis-api', tech: 'Express Node 20', scale: 'Cloud Run min 1', color: 'bg-green-100 dark:bg-green-900/30' },
    { name: 'nabhos-gis-web', tech: 'nginx + Vite SPA', scale: 'Cloud Run min 1', color: 'bg-green-100 dark:bg-green-900/30' },
  ];
  return (
    <div className="my-3 space-y-2 text-xs">
      {/* CI/CD */}
      <div className="flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400 font-mono">
        <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800">GitHub push → main</span>
        <span>→</span>
        <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800">GitHub Actions</span>
        <span>→</span>
        <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800">Cloud Build</span>
        <span>→</span>
        <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800">Artifact Registry</span>
      </div>
      {/* Services */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {services.map((s) => (
          <div key={s.name} className={`rounded border border-gray-200 dark:border-gray-700 p-2 ${s.color}`}>
            <p className="font-mono text-[11px] font-bold text-gray-800 dark:text-gray-200">{s.name}</p>
            <p className="text-[10px] text-gray-500 dark:text-gray-400">{s.tech} · {s.scale}</p>
          </div>
        ))}
      </div>
      {/* Data */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
        {[
          { n: 'Supabase PostgreSQL', sub: '+ PostGIS + pgvector' },
          { n: 'Supabase Storage', sub: 'mission-images bucket' },
          { n: 'GCS', sub: 'Large assets / COGs' },
          { n: 'GIS PostgreSQL', sub: '+ PostGIS' },
        ].map((d) => (
          <div key={d.n} className="rounded border border-gray-200 dark:border-gray-700 p-2 bg-gray-50 dark:bg-gray-800">
            <p className="text-[10px] font-semibold text-gray-700 dark:text-gray-300">{d.n}</p>
            <p className="text-[9px] text-gray-400 dark:text-gray-500">{d.sub}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Panel Content ───────────────────────────────────────────────────────

function ArchitectureContent() {
  return (
    <div className="text-sm">

      {/* ── 1 · Platform Overview ─────────────────────────────────────────── */}
      <Anchor id="s1" />
      <SectionTitle>1 · Platform Overview</SectionTitle>
      <div className="flex flex-wrap gap-2 mb-3">
        <Badge color="blue">Version 2.5</Badge>
        <Badge color="green">Status: Current</Badge>
        <Badge color="gray">July 2026</Badge>
      </div>
      <Para>
        <strong>Nabhos</strong> (from Sanskrit <em>Nabhas</em> = Sky) is a multi-tenant agentic intelligence platform
        that bridges satellite imagery and drone telemetry with actionable field decisions across five industry verticals.
        All five verticals share one configurable 9-step intelligence loop — vertical behaviour is controlled by
        configuration (indices, thresholds, sensor payloads, terminology), not separate codebases.
      </Para>
      <Table
        headers={['Vertical', 'Primary Use Case']}
        rows={[
          ['AgroTech', 'Estate monitoring, crop health, precision spray remediation'],
          ['Oil & Gas', 'Pipeline corridor surveillance, leak indicator detection'],
          ['Infrastructure', 'Construction progress, encroachment, stability monitoring'],
          ['Environmental / Geotech', 'Forest loss, wetland encroachment, carbon credit verification'],
          ['Linear Assets', 'Power line and railway vegetation / obstruction management'],
        ]}
      />
      <SubTitle>Key Non-Functional Commitments</SubTitle>
      <Table
        headers={['Property', 'Target']}
        rows={[
          ['API response (95th percentile)', '< 500 ms'],
          ['Large-file upload (10 GB+)', 'Chunked, resumable, 5 MB chunk'],
          ['Satellite scan cadence', '3–7 day configurable window'],
          ['AI narrative language', "Tenant's configured primary language"],
          ['Tenant data isolation', 'PostgreSQL RLS + JWT claim enforcement'],
          ['Uptime', '99.5% (Cloud Run managed)'],
          ['Data residency', 'Configurable per org node (GDPR / India DPDP)'],
        ]}
      />

      {/* ── 2 · System Boundaries ─────────────────────────────────────────── */}
      <Anchor id="s2" />
      <SectionTitle>2 · System Boundaries</SectionTitle>
      <Para>The Nabhos solution consists of two independently deployable platforms with a clean API integration boundary.</Para>
      <SystemBoundaryDiagram />
      <Table
        headers={['Concern', 'Main Platform', 'GIS Sub-Platform']}
        rows={[
          ['Users, organisations, memberships', '✅', 'Reference only (external IDs)'],
          ['RBAC, permissions, audit logs', '✅', '—'],
          ['Mission workflow state', '✅', '—'],
          ['Notifications, AI help chat', '✅', '—'],
          ['Spatial layers, geometries, boundaries', '—', '✅'],
          ['Feature tables, raster catalog', '—', '✅'],
          ['Tile serving (MVT + raster proxy)', '—', '✅'],
          ['GIS ingestion/processing jobs', '—', '✅'],
          ['Map-side AI outputs (anomaly polygons)', '—', '✅'],
        ]}
      />

      {/* ── 3 · Intelligence Loop ─────────────────────────────────────────── */}
      <Anchor id="s3" />
      <SectionTitle>3 · The 9-Step Intelligence Loop</SectionTitle>
      <Para>
        This is the canonical product workflow. It is identical in structure across all five verticals;
        only the labels, indices, thresholds, and response actions change by vertical configuration.
      </Para>
      <IntelligenceLoopDiagram />
      <SubTitle>Step mapping by vertical</SubTitle>
      <Table
        headers={['Step', 'AgroTech', 'Oil & Gas', 'Infrastructure', 'Environmental', 'Linear Assets']}
        rows={[
          ['1 — Define Area', 'Field + crop metadata', 'Pipeline corridor', 'Asset boundary', 'Protected zone', 'Asset centreline + corridor'],
          ['2 — Satellite', 'NDVI, NDRE, NDWI, LST', 'SWIR methane proxy, subsidence', 'NDBI, NDWI, DEM change', 'NDVI, forest loss, NDWI', 'NDVI (encroachment), thermal'],
          ['3 — AI Analysis', 'Crop stress narrative', 'Leak indicator narrative', 'Construction alert', 'Deforestation alert', 'Growth rate projection'],
          ['4 — Verify', 'Confirm/reject/defer', 'Confirm → OGI survey', 'Confirm → drone evidence', 'Legal authority review', 'Confirm → survey'],
          ['5–6 — Survey', 'Drone inspection + AI', 'OGI camera survey', 'Drone legal-evidence', 'Ground survey', 'Corridor drone patrol'],
          ['7 — Review', 'Agronomist sign-off', 'HSSE safety officer', 'Compliance engineer', 'Environmental officer', 'Asset integrity engineer'],
          ['8 — Remediate', 'Spray / fertilise', 'Emergency Response Plan', 'Enforcement notice', 'Legal evidence package', 'Vegetation clearing'],
          ['9 — Ask Nabhos', 'NLQ', 'NLQ', 'NLQ', 'NLQ', 'NLQ'],
        ]}
      />

      {/* ── 4 · High-Level Architecture ───────────────────────────────────── */}
      <Anchor id="s4" />
      <SectionTitle>4 · High-Level Architecture</SectionTitle>
      <HighLevelDiagram />
      <Note>
        <strong>Storage boundary:</strong> The main platform holds every raw uploaded file indefinitely in MainStorage
        (the <code className="font-mono text-xs">mission-images</code> bucket, path <code className="font-mono text-xs">missions/{'{'}mission_id{'}'}/{'{'}filename{'}'}</code>).
        The GIS sub-platform maintains its own storage for processed derivatives only — it never holds the originals.
        The ingestion worker bridges the two by reading from MainStorage and writing tiles/COGs to GISStorage.
      </Note>

      {/* ── 5 · Component Design ──────────────────────────────────────────── */}
      <Anchor id="s5" />
      <SectionTitle>5 · Component Design</SectionTitle>
      <SubTitle>5.1 Frontend Structure (React + TypeScript)</SubTitle>
      <Code>{`src/frontend/web/src/
├── pages/
│   ├── auth/                   # Login, register, MFA
│   ├── command-center/         # Dashboard, AI Analysis
│   ├── missions/               # List, detail, large-file upload
│   ├── satellite/              # Boundaries map, imagery, anomalies
│   ├── agrotech/               # ✅ 10 pages — full 9-step loop
│   ├── oil-gas/                # ✅ 15 pages — Steps 1–8 + OGI/ERP/Regulatory
│   ├── infrastructure/         # ✅ 10 pages — Steps 1–8 + Encroachment
│   ├── environmental/          # ✅ 10 pages — Steps 1–8 + Carbon Credit
│   ├── linear-assets/          # ✅ 12 pages — Corridor + Encroachment + Obstruction
│   ├── precision/              # ✅  5 pages — VRA, Yield, Suitability, Land, Compliance
│   ├── organization-settings/  # ✅ 11 pages — Users, AI Config, Assets, Audit
│   ├── platform-admin/         # ✅  8 pages — Orgs, Users, Roles, Modules
│   ├── ProfilePage.tsx
│   └── HelpPage.tsx            # 70 structured help articles
├── components/common/
│   ├── Modal.tsx               # Backdrop click + Escape; 20+ modals
│   ├── ChunkedUpload.tsx       # Resumable 5 MB chunked uploader
│   ├── HelpChat.tsx            # AI help chat slide-out
│   ├── SlideOutPanel.tsx       # Right/left slide-out shell
│   └── ...Card, Button, Input, Alert, Spinner, Tooltip
└── services/
    ├── api.ts                  # Typed API client (all verticals)
    └── enterpriseApi.ts        # Multi-tenant extensions`}</Code>
      <SubTitle>5.2 Backend Service Architecture (Node.js + TypeScript)</SubTitle>
      <Code>{`src/backend/api/src/
├── middleware/
│   ├── auth.ts                 # JWT verification + MFA
│   ├── tenant.ts               # RLS tenant context injection
│   └── permissions.ts          # Dynamic RBAC evaluation
├── controllers/                # 28 controllers
│   ├── auth.controller.ts / mfa.controller.ts
│   ├── missions.controller.ts / mission-ingestion.controller.ts
│   ├── chunked-upload.controller.ts
│   ├── anomaly-detection.controller.ts  # Steps 3–4
│   ├── satellite-conversation.controller.ts  # Step 9
│   ├── agrotech-drone.controller.ts     # Steps 5–8
│   ├── report-generation.controller.ts  # Certified PDFs
│   └── gis-platform.controller.ts       # GIS deep-link
├── services/                   # 30+ services
│   ├── google-earth-engine.service.ts   # GEE integration (complete)
│   ├── change-detection.service.ts      # Temporal δ + AI narrative
│   ├── nlq-parser.service.ts           # Intent + boundary resolver
│   ├── satellite-conversation.service.ts # Step 9 context assembly
│   ├── agrotech-drone.service.ts        # Waypoints + remediation calc
│   ├── pdf-engine.service.ts            # Vector PDF with SHA-256 seals
│   ├── help-chat.service.ts
│   ├── notifications/          # Email/SMS/Push/Webhook stubs (⚠ need credentials)
│   └── ai/providers/           # gemini ✅ · cloudflare ✅ · groq ✅ · openrouter ✅
└── jobs/
    └── satellite-monitoring.job.ts  # Scheduled GEE sweep`}</Code>

      {/* ── 6 · Data Architecture ─────────────────────────────────────────── */}
      <Anchor id="s6" />
      <SectionTitle>6 · Data Architecture</SectionTitle>
      <SubTitle>6.1 Main Platform Database (PostgreSQL 15 + PostGIS + pgvector)</SubTitle>
      <Para>Core tables — entity relationship overview:</Para>
      <ERDiagram />
      <SubTitle>Key schemas</SubTitle>
      <Code>{`-- Satellite anomaly with grounded AI narrative
detected_anomalies (
  id UUID PK,
  boundary_id UUID FK satellite_boundaries,
  organization_id UUID FK organizations,
  anomaly_type VARCHAR(50),   -- 'vegetation_loss', 'crop-stress', 'leak-indicator'…
  severity VARCHAR(20),        -- 'critical' | 'high' | 'medium' | 'low'
  confidence_score DECIMAL(5,2),
  ai_narrative TEXT,           -- Step 3: Gemini-generated grounded narrative
  probable_causes JSONB,       -- Step 3: structured probable causes array
  status VARCHAR(20),          -- 'pending' → 'confirmed' | 'rejected' | 'deferred'
  geometry GEOMETRY(Polygon, 4326)
)

-- Immutable Step 4 audit trail (one row per decision; no updates)
anomaly_verifications (
  id UUID PK,
  anomaly_id UUID FK,
  verified_by UUID FK users,
  action VARCHAR(30),          -- 'confirmed' | 'rejected' | 'deferred' | 'escalated'
  rejection_reason VARCHAR(100),
  notes TEXT,
  created_at TIMESTAMPTZ DEFAULT now()
)`}</Code>

      <SubTitle>6.2 GIS Sub-Platform Database (PostgreSQL + PostGIS — portable)</SubTitle>
      <Table
        headers={['Migration', 'Content']}
        rows={[
          ['001_bootstrap.sql', 'Extensions, spatial_assets, layers, assets, ingestion_jobs (retry/dead-letter), processing_artifacts, spatial_observations'],
          ['002_boundaries.sql', 'boundaries, boundary_vertices, boundary_relationships — composite + GiST indexes'],
          ['003_feature_layers.sql', 'layer_schemas, features, feature_annotations, layer_boundary_links — GiST indexes'],
        ]}
      />
      <Ul items={[
        'organization_id and mission_id are external references, not FK constraints to another DB',
        'All geometry via PostGIS (geometry(Geometry, 4326)) with GiST indexes',
        'Object storage references only — no binary blobs in DB (storage_provider, storage_bucket, storage_key, object_uri, checksum)',
        'Multi-tenancy enforced by explicit organization_id column, not Supabase RLS (portable)',
      ]} />

      {/* ── 7 · Integration Architecture ──────────────────────────────────── */}
      <Anchor id="s7" />
      <SectionTitle>7 · Integration Architecture</SectionTitle>

      <SubTitle>7.1 Google Earth Engine (GEE) — Steps 2–3</SubTitle>
      <div className="my-3 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden text-xs">
        {[
          { actor: 'Cloud Scheduler', action: 'Triggers scheduled weekly sweep (03:00 UTC Monday)' },
          { actor: 'GEE Service', action: 'Fetches active boundaries → Sentinel-2 Collection.filterBounds().filterDate()' },
          { actor: 'GEE Service', action: 'Computes NDVI / NDRE / NDWI / LST per vertical config; persists satellite_imagery row' },
          { actor: 'Change Detection', action: 'Loads baseline imagery; computes δ (current NDVI − baseline NDVI)' },
          { actor: 'AI Service', action: 'If δ exceeds threshold → system prompt + grounded DB context → ai_narrative + probable_causes[]' },
          { actor: 'Change Detection', action: 'Inserts detected_anomalies row; dispatches notification' },
        ].map((step, i) => (
          <div key={i} className={`flex gap-3 px-3 py-2 ${i % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800/50'} border-b last:border-b-0 border-gray-100 dark:border-gray-700`}>
            <span className="text-[9px] font-bold text-blue-600 dark:text-blue-400 w-28 flex-shrink-0 mt-0.5">{step.actor}</span>
            <span className="text-gray-700 dark:text-gray-300 leading-snug">{step.action}</span>
          </div>
        ))}
      </div>
      <Note>
        <strong>AI guardrail (Step 3):</strong> The AI service receives only structured DB context — boundary name,
        crop type, NDVI delta, area, period dates. It narrates <em>about</em> real data; it cannot invent findings.
      </Note>

      <SubTitle>7.2 Human Verification — Step 4 Audit Trail</SubTitle>
      <Ul items={[
        'User opens anomaly review modal → GET /api/anomalies/:id/verifications returns prior decision history',
        'User selects action (confirm / reject / defer / escalate) + notes',
        'POST /api/anomalies/:id/verify → BEGIN TRANSACTION → INSERT anomaly_verifications (immutable) → UPDATE detected_anomalies.status → COMMIT',
        'Immutability contract: anomaly_verifications rows are never updated or deleted. Each decision appends a new row.',
      ]} />

      <SubTitle>7.3 Conversational AI — "Ask Nabhos" Flow (Step 9)</SubTitle>
      <div className="my-3 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden text-xs">
        {[
          { from: 'User', to: 'UI', msg: '"Show crop stress in Block 3 since March"' },
          { from: 'UI', to: 'NLQ Parser', msg: 'POST /api/satellite/query — extract intent · location · timeframe · metric' },
          { from: 'NLQ Parser', to: 'PostgreSQL', msg: 'Fuzzy boundary resolve (pg_trgm similarity + ILIKE fallback)' },
          { from: 'NLQ Parser', to: 'PostgreSQL', msg: 'Query anomalies + imagery (date range + boundary)' },
          { from: 'Conversation Svc', to: 'Gemini', msg: 'System prompt + grounded context + user question' },
          { from: 'API', to: 'UI', msg: 'Multi-modal response: narrative + mapHighlight + dataTable + suggestedActions' },
        ].map((step, i) => (
          <div key={i} className={`flex gap-2 px-3 py-2 ${i % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800/50'} border-b last:border-b-0 border-gray-100 dark:border-gray-700`}>
            <span className="text-[9px] font-bold text-teal-600 dark:text-teal-400 w-32 flex-shrink-0 mt-0.5">{step.from} → {step.to}</span>
            <span className="text-gray-700 dark:text-gray-300 leading-snug italic">{step.msg}</span>
          </div>
        ))}
      </div>

      <SubTitle>7.4 Mission Ingestion & Integrity Sealing</SubTitle>
      <Para><strong>Phase 1 — Upload, Hashing & Sealing (Main Platform):</strong></Para>
      <div className="my-2 flex flex-wrap items-center gap-1 text-[10px] font-mono">
        {['Chunked Upload (5 MB)', 'Chunk Assembly + SHA-256', 'Main Object Storage', 'Metadata Extraction (EXIF)', 'Hardware Registry Check', 'Digital Seal (SHA-256)', 'mission_images record', 'Audit Log'].map((s, i, a) => (
          <>
            <span key={s} className="px-2 py-1 rounded bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300">{s}</span>
            {i < a.length - 1 && <span key={`a${i}`} className="text-gray-400">→</span>}
          </>
        ))}
      </div>
      <Para><strong>Phase 2 — GIS Processing & Tile Serving (GIS Sub-Platform):</strong></Para>
      <div className="my-2 flex flex-wrap items-center gap-1 text-[10px] font-mono">
        {['ingestion_jobs queued', 'GIS Worker claims job', 'Read raw asset (MainStorage)', 'Tile gen · COG · Vector extract', 'Write processed (GISStorage)', 'assets table record', 'Tile Proxy Endpoint'].map((s, i, a) => (
          <>
            <span key={s} className="px-2 py-1 rounded bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300">{s}</span>
            {i < a.length - 1 && <span key={`a${i}`} className="text-gray-400">→</span>}
          </>
        ))}
      </div>

      <SubTitle>7.5 Multi-Tenant AI Provider</SubTitle>
      <Table
        headers={['Provider', 'Status', 'Notes']}
        rows={[
          ['Google Gemini', <Badge color="green">✅ Active (default)</Badge>, 'gemini.provider.ts — Gemini 1.5 Pro / Flash'],
          ['Cloudflare Workers AI', <Badge color="green">✅ Active</Badge>, 'cloudflare.provider.ts — serverless inference'],
          ['Groq', <Badge color="green">✅ Active</Badge>, 'groq.provider.ts — low-latency inference'],
          ['OpenRouter', <Badge color="green">✅ Active</Badge>, 'openrouter.provider.ts — multi-model gateway'],
          ['OpenAI GPT-4', <Badge color="amber">⏸ Staged</Badge>, 'openai.provider.ts.bak — can be enabled (see UNFINISHED.md A-1)'],
          ['Anthropic Claude', <Badge color="amber">⏸ Staged</Badge>, 'anthropic.provider.ts.bak — can be enabled'],
        ]}
      />

      <SubTitle>7.6 GIS Platform Integration Contract</SubTitle>
      <Code>{`-- UI Launch (Deep-Link)
GET /api/gis/launch?orgId={uuid}&missionId={uuid}
→ { url: "https://gis.nabhos.com/launch?org=…&mission=…&token=…" }
-- JWT signed with GIS_JWT_SECRET (= JWT_SECRET); 10-min launch tokens

-- Asset Handoff
POST /api/v1/jobs (ingestion_job)
Body: { org_id, mission_id, asset_kind, source_uri: "supabase://mission-images/missions/{id}/{file}" }`}</Code>

      {/* ── 8 · Security Architecture ─────────────────────────────────────── */}
      <Anchor id="s8" />
      <SectionTitle>8 · Security Architecture</SectionTitle>
      <SubTitle>8.1 Multi-Tenant Isolation</SubTitle>
      <Para>
        Every table with tenant-scoped data carries a <code className="font-mono text-xs">tenant_id</code> /
        <code className="font-mono text-xs"> organization_id</code> column. PostgreSQL RLS policies ensure that all queries
        — including ad-hoc SQL — are automatically filtered. A manually constructed UUID bypass attempt returns 403 Forbidden.
      </Para>
      <SubTitle>8.2 Authentication Controls</SubTitle>
      <Table
        headers={['Control', 'Implementation']}
        rows={[
          ['Primary auth', 'JWT (HS256, 1-hour expiry, refresh tokens)'],
          ['MFA', 'TOTP (RFC 6238), SMS OTP (stub — provider TBD), email OTP — configurable per org'],
          ['SSO', 'OIDC / SAML (enterprise plan)'],
          ['API keys', 'Service-to-service (GIS sub-platform, ingestion service)'],
          ['GIS JWT', 'HMAC shared-secret; 10-min launch tokens signed by main platform, verified by GIS API'],
        ]}
      />
      <SubTitle>8.3 RBAC Model</SubTitle>
      <Table
        headers={['Role', 'Scope', 'Capabilities']}
        rows={[
          ['Platform Administrator', 'Global', 'Provision orgs, cross-org analytics, billing'],
          ['Organisation Admin', 'Tenant', 'Configure thresholds, manage users, set AI models'],
          ['Operations & Field Manager', 'Tenant', 'Execute intelligence loop — monitor, verify, plan, query'],
          ['Drone Technician', 'Tenant', 'Upload flight data, manage hardware, execute missions'],
        ]}
      />
      <Note>Role permissions are evaluated at runtime from a database registry (not hardcoded). New roles propagate across active sessions within 60 seconds.</Note>
      <SubTitle>8.4 Data Integrity</SubTitle>
      <Para>Every data artefact carries a SHA-256 cryptographic hash recorded on first ingestion:</Para>
      <Ul items={[
        'Every uploaded file (RGB, Thermal, LiDAR, OGI video)',
        'Every satellite tile stored in the imagery catalog',
        'Every anomaly delta calculation',
        'Every remediation plan',
        'The hash is immutable and referenced in one-click Certified Mission Report PDFs',
      ]} />
      <SubTitle>8.5 Security Controls Summary</SubTitle>
      <Table
        headers={['Category', 'Control']}
        rows={[
          ['Transport', 'TLS 1.3 enforced (Cloud Run managed)'],
          ['Input validation', 'Zod schemas on all API inputs'],
          ['SQL injection', 'Parameterised queries throughout (pg / Zod-validated input)'],
          ['Rate limiting', 'Per-IP and per-tenant token bucket'],
          ['CORS', 'Allowlist of known frontend origins'],
          ['Secrets', 'Google Secret Manager (no secrets in env files in prod)'],
          ['Audit', 'Immutable append-only audit_logs table'],
          ['Storage', 'Signed URLs only — no direct bucket access'],
        ]}
      />

      {/* ── 9 · Deployment Architecture ───────────────────────────────────── */}
      <Anchor id="s9" />
      <SectionTitle>9 · Deployment Architecture</SectionTitle>
      <SubTitle>9.1 Cloud Infrastructure (GCP)</SubTitle>
      <DeploymentDiagram />
      <SubTitle>9.2 Deployment Pipeline</SubTitle>
      <Code>{`git push → main   (.github/workflows/deploy.yml)
  ├── deploy-backend    src/backend/api       → nabhos-api     (Cloud Run)
  ├── deploy-frontend   src/frontend/web      → nabhos-web     (Cloud Run)
  ├── deploy-gis-api    src/gis-platform/api  → nabhos-gis-api (Cloud Run, port 8080)
  └── deploy-gis-web    src/gis-platform/web  → nabhos-gis-web (Cloud Run, port 80)

Auth: Workload Identity Federation — no long-lived service account keys in GitHub`}</Code>
      <SubTitle>Required GCP Secret Manager secrets (representative)</SubTitle>
      <Table
        headers={['Secret', 'Used by', 'Purpose']}
        rows={[
          ['DATABASE_URL', 'nabhos-api', 'Main platform PostgreSQL connection string'],
          ['JWT_SECRET', 'nabhos-api', 'JWT signing secret'],
          ['GEMINI_API_KEY', 'nabhos-api', 'Google Gemini AI key'],
          ['VITE_API_URL', 'nabhos-web', 'Main API URL baked into frontend build'],
          ['GIS_JWT_SECRET', 'nabhos-gis-api', 'Shared HMAC secret for launch JWT (= JWT_SECRET)'],
          ['GIS_PLATFORM_URL', 'nabhos-api', 'GIS web client URL — enables "Open in GIS Platform" deep-link'],
          ['GEE_SERVICE_ACCOUNT_EMAIL', 'nabhos-api', 'GEE service account for live satellite fetch'],
          ['GEE_PRIVATE_KEY', 'nabhos-api', 'GEE private key (registered at code.earthengine.google.com)'],
        ]}
      />
      <SubTitle>9.3 Local Development</SubTitle>
      <Code>{`# Main platform
cp .env.example .env.local
psql "$DATABASE_URL" -f src/database/reset.sql
cd src/backend/api  && npm install && npm run dev   # :8080
cd src/frontend/web && npm install && npm run dev   # :5173

# GIS sub-platform (Docker Compose)
cp src/gis-platform/.env.example src/gis-platform/.env
cd src/gis-platform && docker compose up --build
# Web: http://localhost:3000   API: http://localhost:8081`}</Code>

      {/* ── 10 · GIS Sub-Platform ─────────────────────────────────────────── */}
      <Anchor id="s10" />
      <SectionTitle>10 · GIS Sub-Platform</SectionTitle>
      <Para>
        The GIS sub-platform (<code className="font-mono text-xs">src/gis-platform/</code>) is fully implemented
        (Phases 0–12 complete). It operates as an independent deployable unit.
      </Para>
      <SubTitle>10.1 API Endpoints (30+)</SubTitle>
      <Table
        headers={['Domain', 'Endpoints']}
        rows={[
          ['Health', 'GET /health, GET /metrics'],
          ['Context', 'GET /api/v1/context/snapshot/:orgId/:missionId'],
          ['Boundaries', 'Full CRUD + GeoJSON import/export + relationships'],
          ['Layers', 'Full CRUD + feature management + boundary links'],
          ['Assets', 'Catalog + storage URL proxy + raster tile proxy'],
          ['Jobs', 'Queue + claim + progress + retry + dead-letter'],
          ['Tiles', 'GET /api/v1/tiles/:layerId/:z/:x/:y.mvt — ST_AsMVT vector tiles'],
          ['Spatial', 'Boundaries intersects + features near'],
          ['Observations', 'Full CRUD + GeoJSON export + soft-delete'],
          ['AI', 'Summary (counts by severity/status) + geo-referenced anomaly list'],
        ]}
      />
      <SubTitle>10.2 Web Client Pages (13 pages)</SubTitle>
      <Table
        headers={['Route', 'Page']}
        rows={[
          ['/', 'Home + seeded context snapshot'],
          ['/map', 'MapLibre map: boundaries + MVT tiles + observations + draw tool'],
          ['/boundaries', 'List + filter + export + relationship CRUD'],
          ['/layers', 'Layer browser + feature management'],
          ['/observations', 'List + filter + edit + dismiss + GeoJSON export'],
          ['/assets', 'Asset catalog + URL resolver + register'],
          ['/jobs', 'Job queue + live polling + progress bar'],
          ['/spatial', 'Spatial query sandbox'],
          ['/ai', 'AI summary + geo-anomaly list'],
          ['/metrics', 'Live API + DB pool metrics'],
        ]}
      />
      <SubTitle>10.3 Key Design Constraints</SubTitle>
      <Ul items={[
        'Separate DB — no cross-DB foreign keys',
        'PostGIS-first — all geometry via ST_ functions',
        'API-first — web client never accesses DB directly',
        'Object storage references only — no binary blobs in DB',
        'Portable schema — no Supabase-specific features in core schema',
        'JWT/JWKS auth — configurable bearer token or trusted-header fallback',
      ]} />

      {/* ── 11 · Design System ────────────────────────────────────────────── */}
      <Anchor id="s11" />
      <SectionTitle>11 · Design System</SectionTitle>
      <Table
        headers={['Layer', 'Technology']}
        rows={[
          ['Frontend', 'React 18, TypeScript, Vite, TailwindCSS'],
          ['Map (main)', 'Leaflet (boundary draw, polygon visualization)'],
          ['Map (GIS)', 'MapLibre GL JS (MVT tiles, raster overlays)'],
          ['Icons', 'Lucide React'],
          ['Charts', 'Recharts'],
          ['State', 'Zustand + React Query'],
        ]}
      />
      <SubTitle>Palette Variants</SubTitle>
      <Table
        headers={['Variant', 'Primary Colours', 'Best For']}
        rows={[
          ['Modern', 'Deep Ocean Blue #0F1B2D · Electric Blue #0066FF · Sky Blue #00A3FF', 'Tech-forward enterprise'],
          ['Organic', 'Deep Sage #1B4332 · Olive Green #386641 · Terracotta #BC4B51', 'Agricultural / nature focus'],
        ]}
      />
      <SubTitle>Core Design Tokens</SubTitle>
      <Code>{`Font:   Inter (display) + JetBrains Mono (code/data)
Scale:  8-point base grid
Radius: 4 / 8 / 12 / 16 / 24 px tiers
Space:  4 / 8 / 12 / 16 / 24 / 32 / 48 / 64 / 80 / 96 px
Shadow: sm (card) / md (dropdown) / lg (modal) / xl (focus overlay)`}</Code>
      <SubTitle>Component Standards</SubTitle>
      <Ul items={[
        'Buttons: Primary (filled) / Secondary (outlined) / Ghost — all with loading state',
        'Cards: Glassmorphism surface (backdrop-filter: blur(10px))',
        'Status badges: Colour-coded — critical (red), high (orange), medium (yellow), low (blue)',
        'Dark mode: Full dark mode via Tailwind dark: class tokens',
        'Accessibility: WCAG AA minimum contrast, keyboard navigation, prefers-reduced-motion',
        'Responsive: Breakpoints at 640 / 768 / 1024 / 1280 / 1536 px',
      ]} />

      {/* ── 12 · Implementation Status ────────────────────────────────────── */}
      <Anchor id="s12" />
      <SectionTitle>12 · Implementation Status</SectionTitle>
      <SubTitle>Main Platform</SubTitle>
      <Table
        headers={['Component', 'Status', 'Notes']}
        rows={[
          ['Auth (JWT + MFA + RBAC)', <Badge color="green">✅ Done</Badge>, 'TOTP, SMS (stub), email OTP'],
          ['Mission CRUD', <Badge color="green">✅ Done</Badge>, 'Full lifecycle'],
          ['Chunked upload (>10 GB)', <Badge color="green">✅ Done</Badge>, '5 MB chunks, resumable'],
          ['Platform Admin UI', <Badge color="green">✅ Done</Badge>, 'Org provisioning, role registry'],
          ['Satellite boundary CRUD', <Badge color="green">✅ Done</Badge>, 'PostGIS polygon, GeoJSON import'],
          ['AgroTech UI (10 pages)', <Badge color="green">✅ Done</Badge>, 'Full 9-step + Operations Dashboard'],
          ['Drone Steps 5–8', <Badge color="green">✅ Done</Badge>, 'Auto-grid waypoints · AI analysis · digital seals · remediation calc'],
          ['Oil & Gas vertical (15 pages)', <Badge color="green">✅ Done</Badge>, 'Steps 1–8 + OGI, ERP, Regulatory, Exploration, Value'],
          ['Infrastructure vertical (10 pages)', <Badge color="green">✅ Done</Badge>, 'Steps 1–8 + Encroachment, Construction Progress'],
          ['Linear Assets vertical (12 pages)', <Badge color="green">✅ Done</Badge>, 'Corridor · encroachment modelling · obstruction triage (<6h SLA)'],
          ['Environmental vertical (10 pages)', <Badge color="green">✅ Done</Badge>, 'Steps 1–8 + Geohazard Risk, Carbon Credit Verification'],
          ['Precision AgroTech add-on (5 pages)', <Badge color="green">✅ Done</Badge>, 'VRA, yield, suitability, chemical compliance, land expansion'],
          ['Step 2 — GEE live satellite fetch', <Badge color="green">✅ Done</Badge>, 'google-earth-engine.service.ts · satellite-monitoring.job.ts'],
          ['Step 3 — AI anomaly narratives', <Badge color="green">✅ Done</Badge>, 'change-detection.service.ts; grounded Gemini narrative'],
          ['Step 4 — Human verification audit trail', <Badge color="green">✅ Done</Badge>, 'anomaly_verifications table; immutable append-only'],
          ['Step 9 — Conversational NLQ', <Badge color="green">✅ Done</Badge>, 'nlq-parser + satellite-conversation service + ConversationalAnalysisPage'],
          ['Certified PDF reports', <Badge color="green">✅ Done</Badge>, 'pdf-engine + report-generation (SHA-256 Mission / Anomaly / Remediation / Carbon)'],
          ['AI provider config (BYOK)', <Badge color="green">✅ Done</Badge>, 'AIConfigurationPage; Gemini, Cloudflare, Groq, OpenRouter active'],
          ['Notification delivery channels', <Badge color="amber">⚠️ Stub</Badge>, 'Email/SMS/push/webhook dispatch stubs; credentials required — see UNFINISHED.md'],
          ['Mobile field app', <Badge color="gray">📋 Planned</Badge>, 'React Native offline-first for on-ground operatives'],
        ]}
      />
      <SubTitle>GIS Sub-Platform</SubTitle>
      <Table
        headers={['Component', 'Status']}
        rows={[
          ['Express API (30+ endpoints)', <Badge color="green">✅ Complete (Phase 12)</Badge>],
          ['MapLibre web client (13 pages)', <Badge color="green">✅ Complete (Phase 12)</Badge>],
          ['PostGIS schema (3 migrations)', <Badge color="green">✅ Complete (Phase 12)</Badge>],
          ['ST_AsMVT vector tile endpoint', <Badge color="green">✅ Complete</Badge>],
          ['Raster tile proxy', <Badge color="green">✅ Complete</Badge>],
          ['GIS ingestion worker + retry/dead-letter', <Badge color="green">✅ Complete</Badge>],
          ['JWT/JWKS auth middleware', <Badge color="green">✅ Complete</Badge>],
          ['Docker Compose deployment', <Badge color="green">✅ Complete</Badge>],
          ['Structured JSON logging + /metrics endpoint', <Badge color="green">✅ Complete</Badge>],
          ['Backup/restore scripts', <Badge color="green">✅ Complete</Badge>],
        ]}
      />

      {/* ── 13 · Key Architectural Decisions ──────────────────────────────── */}
      <Anchor id="s13" />
      <SectionTitle>13 · Key Architectural Decisions</SectionTitle>
      <Table
        headers={['Decision', 'Choice', 'Rationale']}
        rows={[
          ['GIS as separate solution', 'Separate src/gis-platform/', 'Distinct deployment, scaling, and schema concerns; prevents tight coupling'],
          ['GIS DB separation', 'Separate PostgreSQL + PostGIS DB', 'Clean boundary; GIS schema evolves independently; portable away from any host'],
          ['Supabase as initial host only', 'Not locked in', 'Schema uses plain PostgreSQL; Supabase Auth/RLS not used as core contract in GIS'],
          ['Vertical behaviour via config', 'JSONB metadata, config-driven indices', 'Five verticals share one codebase; no code deployment to add a vertical'],
          ['AI grounding', 'Gemini receives DB context only', 'Eliminates hallucination; AI narrates data, does not invent findings'],
          ['Anomaly verifications immutable', 'Append-only table', 'Complete audit trail for regulatory compliance; reversals are new rows, not updates'],
          ['Chunked uploads', '5 MB chunks, resumable', 'LiDAR and OGI video can exceed 10 GB; network recovery essential'],
          ['SHA-256 integrity seals', 'On every artefact at ingestion', 'Legal, insurance, and regulatory admissibility of AI findings'],
          ['Dynamic RBAC', 'DB-backed role registry', 'New roles propagate in ≤60 s without code deployment'],
          ['Object storage refs in GIS DB', 'object_uri + storage_key', 'Avoids binary blobs in PostgreSQL; storage backend swappable'],
          ['PostGIS-first geometry', 'ST_ functions, GiST indexes', 'Native spatial queries without ORM overhead'],
        ]}
      />

      {/* ── 14 · Document Registry ────────────────────────────────────────── */}
      <Anchor id="s14" />
      <SectionTitle>14 · Document Registry</SectionTitle>
      <SubTitle>Canonical (always current)</SubTitle>
      <Table
        headers={['Document', 'Role']}
        rows={[
          ['docs/ARCHITECTURE.md', 'System architecture, data design, integration contracts, deployment, implementation status'],
          ['specs/Nabhos-Functional-Capabilities.md', 'Product capability reference for all 5 verticals'],
          ['specs/REQUIREMENTS-Nabhos-SatelliteToDrone-V0.3.md', 'AgroTech 9-step acceptance criteria and navigation architecture'],
          ['docs/planning/GIS_CURRENT_STATUS.md', 'GIS sub-platform full inventory (endpoints, pages, migrations, seeds, deployment)'],
          ['docs/ROADMAP.md', 'Forward roadmap — next features, priorities, sequencing'],
          ['docs/DEPLOYMENT.md', 'Cloud Run deployment playbook (GCP, GitHub Actions, Workload Identity)'],
          ['docs/FRONTEND.md', 'End-user frontend guide'],
        ]}
      />
      <SubTitle>Supporting / Historical (do not treat as current spec)</SubTitle>
      <Table
        headers={['Document', 'Status', 'Note']}
        rows={[
          ['specs/REQUIREMENTS-Nabhos-SatelliteToDrone-V0.1.md', '⚠️ Archived', 'Superseded by V0.3'],
          ['specs/REQUIREMENTS-Nabhos-SatelliteToDrone-V0.2.md', '⚠️ Archived', 'Superseded by V0.3'],
          ['docs/planning/GIS_SOLUTION_DECISION_LOG.md', '📚 Historical', 'GIS architectural decisions — GIS is fully built'],
          ['docs/planning/GIS_IMPLEMENTATION_PLAN.md', '📚 Historical', 'GIS phase plan — all phases complete'],
          ['docs/planning/AGROTECH-IMPLEMENTATION-PLAN.md', '📚 Historical', 'AgroTech UI build plan — all phases complete'],
          ['docs/planning/GEE_IMPLEMENTATION.md', '📚 Historical', 'GEE integration design notes — superseded by live implementation'],
          ['docs/planning/LOCAL_DEVELOPMENT.md', '📚 Historical', 'Early Docker dev setup — see §9.3'],
          ['docs/planning/GCP_DEPLOYMENT.md', '📚 Historical', 'Early GCP notes — see docs/DEPLOYMENT.md'],
        ]}
      />

      {/* Footer */}
      <div className="mt-8 pt-4 border-t border-gray-200 dark:border-gray-700 text-center">
        <p className="text-[10px] text-gray-400 dark:text-gray-500">
          Nabhos Platform · Architecture & Design Reference · Version 2.5 · July 2026
        </p>
      </div>
    </div>
  );
}

// ─── Panel shell ──────────────────────────────────────────────────────────────

interface ArchitecturePanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ArchitecturePanel({ isOpen, onClose }: ArchitecturePanelProps) {
  const [activeNav, setActiveNav] = useState('s1');
  const contentRef = useRef<HTMLDivElement>(null);

  // Sync active nav indicator on scroll
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const handleScroll = () => {
      for (const item of [...NAV].reverse()) {
        const anchor = el.querySelector(`#${item.id}`);
        if (anchor) {
          const top = anchor.getBoundingClientRect().top - el.getBoundingClientRect().top;
          if (top <= 80) { setActiveNav(item.id); break; }
        }
      }
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [isOpen]);

  const scrollTo = (id: string) => {
    setActiveNav(id);
    const container = contentRef.current;
    const anchor = container?.querySelector(`#${id}`);
    if (anchor && container) {
      const delta = anchor.getBoundingClientRect().top - container.getBoundingClientRect().top;
      container.scrollBy({ top: delta - 16, behavior: 'smooth' });
    }
  };

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel — full-height, 70 vw on desktop */}
      <div
        className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[70vw] max-w-5xl bg-white dark:bg-gray-900 shadow-2xl flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label="Solution Architecture"
      >

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 bg-gray-50 dark:bg-gray-800">
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Nabhos Platform — Architecture & Design Reference
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Version 2.5 · July 2026 · Single authoritative reference
            </p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/docs/ARCHITECTURE.md"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Open raw markdown in new tab"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Raw</span>
            </a>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close architecture panel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body — two column: sticky nav + scrollable content */}
        <div className="flex flex-1 overflow-hidden">

          {/* Left nav */}
          <nav className="hidden sm:flex flex-col w-52 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 overflow-y-auto py-3">
            {NAV.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => scrollTo(item.id)}
                className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${
                  activeNav === item.id
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-semibold border-r-2 border-blue-500'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-white/60 dark:hover:bg-gray-700/40'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Scrollable content */}
          <div ref={contentRef} className="flex-1 overflow-y-auto px-5 py-4">
            <ArchitectureContent />
          </div>
        </div>
      </div>
    </>
  );
}

// Made with Bob
